import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '../../lib/utils'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { useToast } from '../../components/ui/useToast'

export function AdminLoginPage() {
  const navigate = useNavigate()
  const { notify } = useToast()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    master_key: '',
  })
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Check if already logged in as admin
  useEffect(() => {
    const checkAdminSession = async () => {
      try {
        const res = await fetch('/api/admin/session', { credentials: 'include' })
        if (res.ok) {
          navigate('/admin/dashboard')
        }
      } catch {
        // Not logged in, continue
      }
    }
    checkAdminSession()
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Login failed')
      }

      notify({ kind: 'success', title: 'Admin Access Granted', message: 'Welcome to the global admin portal.' })
      navigate('/admin/dashboard')
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials and master key.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-gray-50 dark:bg-gray-950">
      {/* Left: Branding */}
      <div className="relative hidden md:flex flex-col p-10 bg-gradient-to-br from-orange-600 via-orange-700 to-red-600 text-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0 gradient-mesh opacity-30" />
        {/* Top brand */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center shadow-soft-lg">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-white to-orange-100 flex items-center justify-center text-orange-600 font-bold text-lg">SA</div>
          </div>
          <div className="text-xl font-bold">Global Admin Portal</div>
        </div>
        {/* Centered hero content */}
        <div className="relative z-10 flex-1 flex items-center">
          <div className="max-w-md space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">Manage tenants, monitor system health, and control platform access.</h2>
            <p className="text-lg text-white/90 leading-relaxed">Secure global administration for studiomgr360. Requires master key authentication.</p>
            <div className="flex flex-col gap-3 pt-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-white"></div>
                <span className="text-sm text-white/80">Three-factor authentication</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-white"></div>
                <span className="text-sm text-white/80">Tenant management</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-white"></div>
                <span className="text-sm text-white/80">System monitoring</span>
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="relative z-10 text-sm text-white/70">© {new Date().getFullYear()} studiomgr360. All rights reserved.</div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center px-6 py-12 md:px-12 bg-white dark:bg-gray-900">
        <div className="w-full max-w-md animate-fade-slide-up">
          <div className="mb-8">
            <div className="md:hidden text-center mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mx-auto mb-3 shadow-soft">
                <span className="text-white font-bold text-lg">SA</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">Global Admin</div>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Admin Access</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Sign in with master key and credentials</p>
          </div>

          {/* Security Notice */}
          <div className="mb-6 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 text-orange-800 dark:text-orange-300 px-4 py-3 rounded-xl text-sm">
            <div className="font-semibold mb-1">Security Notice</div>
            <div className="text-orange-700 dark:text-orange-400">This portal requires master key authentication. Never share your master key via email, chat, or insecure channels.</div>
          </div>

          {error && (
            <div className="mb-6 bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-700 dark:text-error-400 px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="form-label">Email address</label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                placeholder="admin@example.com"
                value={formData.email}
                onChange={handleChange}
                autoComplete="username"
              />
            </div>
            <div>
              <label htmlFor="password" className="form-label">Password</label>
              <Input
                id="password"
                name="password"
                type="password"
                required
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                autoComplete="current-password"
              />
            </div>
            <div>
              <label htmlFor="master_key" className="form-label">Master Key</label>
              <Input
                id="master_key"
                name="master_key"
                type="password"
                required
                placeholder="Enter master key"
                value={formData.master_key}
                onChange={handleChange}
                autoComplete="off"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Required for admin portal access</p>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className={cn(
                'w-full h-12 text-base bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700',
                isLoading && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isLoading ? 'Authenticating...' : 'Sign in to Admin Portal'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            <a href="/login" className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium transition-colors">
              Return to user login
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
